import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

/**
 * Rol S: reenvío manual de alerta de vigilancia (crear + TTL desde ahora + FCM).
 */
const ReenvioAlertaVigilancia = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [destinatarios, setDestinatarios] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [usuarioDestino, setUsuarioDestino] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (environment.isProduction) {
      navigate('/dashboard-supervision', { replace: true });
      return;
    }
    if (user && user.rol !== 'S') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const cargar = useCallback(async () => {
    if (!user?.username || user?.rol !== 'S') return;
    setLoading(true);
    setError('');
    try {
      const url = new URL(getApiUrl('ALERTA_VIGILANCIA', 'ADMIN_STATUS'));
      url.searchParams.set('username', user.username);
      const res = await fetch(url.toString());
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo cargar destinatarios.');
        setDestinatarios([]);
        return;
      }
      setCfg(data.data?.cfg || null);
      const lista = data.data?.destinatarios || [];
      setDestinatarios(lista);
      setUsuarioDestino((prev) => prev || (lista[0] && lista[0].usuario) || '');
    } catch (err) {
      setError('Error de conexión al cargar destinatarios.');
      setDestinatarios([]);
    } finally {
      setLoading(false);
    }
  }, [user?.username, user?.rol]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const reenviar = async () => {
    if (!usuarioDestino) {
      setError('Selecciona un destinatario.');
      return;
    }
    setEnviando(true);
    setError('');
    setResultado(null);
    try {
      const res = await fetch(getApiUrl('ALERTA_VIGILANCIA', 'ADMIN_REENVIAR'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          usuarioDestino
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo reenviar la alerta.');
        return;
      }
      setResultado(data.data);
    } catch (err) {
      setError('Error de conexión al reenviar la alerta.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reenvío de notificación</h1>
          <p className="text-sm text-gray-600 mt-1">
            Crea una alerta ahora (TTL desde este momento) y envía el push. Solo rol supervisión.
          </p>
        </div>

        {cfg && (
          <p className="text-xs text-gray-500">
            TTL: {cfg.ttlMin} min · ventana Gestor: {cfg.ventana_inicio}–{cfg.ventana_fin}
            {cfg.enabled ? '' : ' · job del Gestor inactivo (el reenvío igual funciona)'}
          </p>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {resultado && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 space-y-1">
            <p className="font-medium">{resultado.mensaje}</p>
            <p>Alerta #{resultado.alertaId} · {resultado.usuario}</p>
            <p className="break-all font-mono text-xs">{resultado.path}</p>
            <p>Push: {resultado.pushes}/{resultado.tokens} · vence {resultado.vence_en}</p>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-4">
          <label className="block text-sm font-medium text-gray-700" htmlFor="destinatario-alerta">
            Destinatario
          </label>
          <select
            id="destinatario-alerta"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={usuarioDestino}
            onChange={(e) => setUsuarioDestino(e.target.value)}
            disabled={loading || enviando || destinatarios.length === 0}
          >
            {destinatarios.length === 0 ? (
              <option value="">No hay destinatarios activos</option>
            ) : (
              destinatarios.map((d) => (
                <option key={d.usuario} value={d.usuario}>
                  {d.usuario} (puesto {d.id_generado})
                </option>
              ))
            )}
          </select>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={reenviar}
              disabled={loading || enviando || !usuarioDestino}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {enviando ? 'Enviando…' : 'Reenviar notificación'}
            </button>
            <button
              type="button"
              onClick={cargar}
              disabled={loading || enviando}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Actualizar lista
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReenvioAlertaVigilancia;
